import styled, { keyframes } from 'styled-components'
import {
    svgStroke,
    numbers, belowPhone, belowTablet, fromTablet, fromDesktop
} from 'utilities/styled'
import { Grid } from 'components/grid'
import { CloseButton as CloseButtonBase } from 'components/button'

/* Content and Details */
import ImageLogoBase from 'components/logo'
import { tiptapContentCss } from 'components/tiptap/s'
import { SkeletonStrings as SkeletonStringsBase } from 'components/skeleton'
import FileNameIconBase from 'components/file-name-icon'
import Paragraph from 'components/typography/paragraph'
import ContextMenuBase from 'widgets/context-menu/flat'

export const Layout = styled(Grid)`
    position: relative;
    overflow: hidden;

    grid-template-columns: minmax(0, 1fr) 400px;

    ${belowTablet`
        grid-template-rows: var(--content-height) calc(100% - var(--content-height));
        grid-template-columns: unset;
    `}
`

export const CloseButton = styled(CloseButtonBase)`
    ${belowTablet`
        top: 16px;
        right: 16px;
    `}

    ${fromTablet`
        top: -8px;
        right: -8px;
    `}
`

/* Content */

export const Main = styled.main`
    container-type: inline-size;
    container-name: smart-preview;

    position: relative;
    overflow: auto;

    border-radius: 8px 0 0 8px;
    background-color: var(--huma-color-surface-minimal);

    .no-select > & {
        user-select: none;
    }

    ${belowPhone`
        border-radius: 16px 16px 0 0;
        width: 100%;
    `}
`

export const SkeletonTitle = styled(SkeletonStringsBase).attrs(attrs => ({
    ...attrs,
    size: 32,
    length: 50
}))`
    margin-block-end: 28px;
`

export const SkeletonH2 = styled(SkeletonStringsBase).attrs(attrs => ({
    ...attrs,
    size: 24,
    length: 35
}))`
    margin-block-end: 16px;
`

export const SkeletonParagraph = styled.div`
    &.indent {
        margin-inline-start: 24px;
    }

    &:not(:last-child) {
        margin-block-end: 24px;
    }
`

export const SkeletonStrings = styled(SkeletonStringsBase).attrs(attrs => ({
    ...attrs,
    size: 18
}))`
    > span {
        margin-block-end: 4px;
    }
`

export const ImageLogo = styled(ImageLogoBase)`
    margin-block-end: 32px;
`

const contentIn = keyframes`
    0% {
        opacity: 0;
        transform: translateY(16px);
    }
    100% {
        opacity: 1;
        transform: translateY(0);
    }
`

export const Content = styled.div`
    margin-block: 56px 32px;
    margin-inline: auto;
    border-radius: 16px;
    width: min(210mm, calc(100% - 32px));
    min-height: 297mm;
    padding: 32px;
    background-color: var(--huma-color-surface-default);
    box-shadow: var(--huma-shadow-highest);

    animation: ${contentIn} .3s ease-out forwards;

    &.bordered {
        border: 4px dashed var(--huma-color-border-default);
    }

    @container smart-preview (width < 210mm) {
        border-radius: 0;
        margin: 0;
        width: 100%;
        padding-inline: 24px;
    }

    @media (${numbers.tablet}px <= width <= calc(210mm + 400px + 32px)) {
        min-height: 100%;
    }

    ${fromDesktop`
        padding: 64px;
    `}

    ${belowTablet`
        min-height: calc(var(--content-height) + 88px); /* margin-block */
    `}

    @media (width <= 210mm) {
        min-height: 100%;
    }
`

export const Body = styled.div`
    ${tiptapContentCss}

    :last-child {
        margin-bottom: 0;
        padding-bottom: 0;
    }
`

/* Details */

export const Aside = styled.aside`
    overflow-y: auto;
    overflow-x: clip;

    border-radius: 0 8px 8px 0;
    padding: 32px;
    background-color: var(--huma-color-surface-default);

    .no-select > & {
        user-select: none;
    }

    /* A4 - aside width - modal margin */
    @media (${numbers.tablet}px <= width <= calc(210mm + 400px + 32px)) {
        main ~ & {
            position: relative;

            box-shadow: -8px 0 16px var(--huma-color-shadow);
        }
    }

    ${belowTablet`
        border-radius: 0;
        padding: 24px 24px 48px;
        background-color: var(--huma-color-surface-elevated);
    `}
`

export const Sections = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 24px;

    max-width: 400px;
`

export const Section = styled.section`
    &.loading {
        height: 160px;
        position: relative;
    }
`

export const Kicker = styled.div`
    margin-block-end: 16px;

    ${fromTablet`
        position: relative;
    `}
`

export const FileNameIcon = styled(FileNameIconBase)`
    font-size: 20px;
    font-weight: 500;

    svg.feather {
        ${svgStroke()}
    }
`

export const FakeLabel = styled(Paragraph)`
    padding-inline-start: 40px;
`

export const ContextMenu = styled(ContextMenuBase)`
    overflow: hidden;
    border: 1px solid var(--huma-color-border-default);
    border-radius: 8px;
`